import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';
import SkillCreateSlideout from '../skill/SkillCreateSlideout';
import SkillPromotionCard from '../skill/SkillPromotionCard';
import SkillEvalPanel from '../skill/SkillEvalPanel';

/**
 * ExteletPolicySkills — ExteLet governance-policy management for the BUILD tab.
 * A thin loader + grid over the SHARED <SkillPromotionCard> (identical card for
 * every agentlet type + phase). The card owns all promotion mechanics + the
 * review/eval slideouts; this surface just supplies the policy-skill list,
 * Configure → policy-config, and the eval body (shared SkillEvalPanel — same component the native LET eval uses).
 *
 * @param {Object}   props.agent     needs id, name
 * @param {Function} props.onChanged called after any mutation (parent reloads gate)
 */
export default function ExteletPolicySkills({ agent, onChanged }) {
  const navigate = useNavigate();
  const toastCtx = useToast();
  const addToast = useCallback(
    (arg, type, duration) => toastCtx?.addToast?.(arg, type, duration),
    [toastCtx],
  );

  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    if (!agent?.id) return;
    setLoading(true);
    try {
      const res = await api.getAgentSkills?.(agent.id);
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setSkills(list.filter((s) => s.skillType === 'policy_skill'));
    } finally {
      setLoading(false);
    }
  }, [agent?.id]);

  useEffect(() => { load(); }, [load]);

  const reloadAll = useCallback(async () => {
    await load();
    onChanged?.();
  }, [load, onChanged]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted">
          Protection Policy Skills
        </span>
        <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>
          + Add protection policy
        </Button>
      </div>

      {/* PROVE7-020: gate the spinner on INITIAL load only. A background refetch
          (reloadAll after an eval run) must NOT blank the grid, or SkillPromotionCard
          unmounts and its evalOpen slideout closes. Cards reconcile by key=s.id, so
          keeping them mounted preserves the open Eval slideout — matches native LET. */}
      {loading && skills.length === 0 ? (
        <p className="text-[12px] text-v2-ink-muted m-0">Loading…</p>
      ) : skills.length === 0 ? (
        <p className="text-[12px] text-v2-ink-muted m-0">
          No protection policy yet. Add one, <strong>Configure</strong> its rules, then generate &amp; run an
          <strong> eval</strong> on it to clear the Build → Decide gate.
        </p>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
          {skills.map((s) => (
            <SkillPromotionCard
              key={s.id}
              agentId={agent.id}
              skill={s}
              surface="build"
              onChanged={reloadAll}
              onConfigure={() => navigate(
                `/policy-config?skillId=${s.id}&agentId=${agent.id}`
                + `&agentName=${encodeURIComponent(agent.name || '')}&stage=source`)}
              renderEval={(sk) => <SkillEvalPanel agentId={agent.id} skill={sk} onChanged={reloadAll} />}
            />
          ))}
        </div>
      )}

      <SkillCreateSlideout
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        agentId={agent.id}
        agentName={agent.name}
        initialType="policy_skill"
        onCreated={() => { setAddOpen(false); reloadAll(); }}
        addToast={addToast}
      />
    </div>
  );
}
